import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import DecryptedText from './DecryptedText';
import '../styles/horizontal-scroll-gallery.css';

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov'];

const getMediaTypeFromSrc = (src) => {
  if (!src || typeof src !== 'string') return 'image';
  const extension = src.split('.').pop()?.toLowerCase();
  return VIDEO_EXTENSIONS.includes(extension) ? 'video' : 'image';
};

const HorizontalScrollGallery = ({
  images = [],
  showLabels = true,
  enableLightbox = false,
  onImageClick = () => {},
}) => {
  const sectionRef = useRef(null);
  const wrapperRef = useRef(null);
  const progressBarRef = useRef(null);

  const defaultImages = [
    '/project-images/project-img-1.svg',
    '/project-images/project-img-2.svg',
    '/project-images/project-img-3.svg',
    '/project-images/project-img-4.svg',
    '/project-images/project-img-1.svg',
  ];

  const sanitizedImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const sourceItems = sanitizedImages.length > 0 ? sanitizedImages : defaultImages;

  const mediaItems = sourceItems
    .map((item, index) => {
      if (typeof item === 'string') {
        return {
          src: item,
          type: getMediaTypeFromSrc(item),
          alt: `Project snapshot ${index + 1}`,
          poster: '',
          loop: true,
          autoPlay: true,
          muted: true,
        };
      }

      if (typeof item === 'object' && item !== null) {
        const src = item.src || '';
        if (!src) return null;
        return {
          src,
          type: item.type || getMediaTypeFromSrc(src),
          alt: item.alt || `Project snapshot ${index + 1}`,
          poster: item.poster || item.thumbnail || '',
          loop: item.loop ?? true,
          autoPlay: item.autoPlay ?? true,
          muted: item.muted ?? true,
        };
      }

      return null;
    })
    .filter((media) => !!media);

  const handleSnapshotClick = (media) => {
    if (!enableLightbox || typeof onImageClick !== 'function') return;
    onImageClick(media);
  };

  useEffect(() => {
    // Early return if refs aren't ready
    if (!sectionRef.current || !wrapperRef.current) return;

    const snapshotsSection = sectionRef.current;
    const wrapper = wrapperRef.current;

    // Create progress bar dynamically
    const progressBarContainer = document.createElement('div');
    progressBarContainer.className = 'snapshots-progress-bar';

    // Add progress indicators
    const indicatorCount = Math.max((mediaItems.length || 1) * 4, 16);
    for (let i = 0; i < indicatorCount; i++) {
      const indicator = document.createElement('div');
      indicator.className = 'progress-indicator';
      progressBarContainer.appendChild(indicator);
    }

    // Add progress bar
    const progressBar = document.createElement('div');
    progressBar.className = 'progress-bar';
    progressBarContainer.appendChild(progressBar);

    snapshotsSection.appendChild(progressBarContainer);
    progressBarRef.current = progressBar;

    // Initialize scroll animation after a short delay
    let cleanupScroll;
    const initTimeout = setTimeout(() => {
      cleanupScroll = initSnapshotsScroll();
    }, 100);

    function initSnapshotsScroll() {
      // Calculate dimensions for horizontal scroll
      const calculateDimensions = () => {
        const wrapperWidth = wrapper.scrollWidth;
        const viewportWidth = window.innerWidth;
        return -(wrapperWidth - viewportWidth);
      };

      let moveDistance = calculateDimensions();

      // Safari and iOS detection
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // Create ScrollTrigger
      const scrollTriggerInstance = ScrollTrigger.create({
        trigger: snapshotsSection,
        start: 'top top',
        end: () => `+=${window.innerHeight * 5}px`,
        pin: true,
        pinSpacing: true,
        scrub: isSafari && isIOS ? 0.5 : 1,
        invalidateOnRefresh: true,
        onRefresh: () => {
          moveDistance = calculateDimensions();
        },
        onUpdate: (self) => {
          const progress = self.progress;
          const currentTranslateX = progress * moveDistance;

          gsap.set(wrapper, {
            x: currentTranslateX,
            force3D: true,
            transformOrigin: 'left center',
          });

          if (progressBarRef.current) {
            gsap.set(progressBarRef.current, {
              width: `${progress * 100}%`,
            });
          }
        },
      });

      // Handle resize
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          ScrollTrigger.refresh();
        }, 250);
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (scrollTriggerInstance) {
          scrollTriggerInstance.kill();
        }
        if (progressBarContainer && progressBarContainer.parentNode) {
          progressBarContainer.remove();
        }
      };
    }

    // Cleanup on unmount
    return () => {
      clearTimeout(initTimeout);
      if (typeof cleanupScroll === 'function') {
        cleanupScroll();
      }
      if (progressBarRef.current && progressBarRef.current.parentNode) {
        progressBarRef.current.parentNode.remove();
      }
    };
  }, [mediaItems.length]);

  return (
    <section ref={sectionRef} className="project-snapshots">
      {showLabels && (
        <div className="home-spotlight-bottom-bar">
          <div className="container">
            <div className="mono hover-effect-gallery">
              <span>&#9654;</span> <DecryptedText text="Interface Study" animateOn="view" speed={80} />
            </div>
            <div className="mono hover-effect-gallery">
              / <DecryptedText text="Moodboard" animateOn="view" speed={80} />
            </div>
          </div>
        </div>
      )}

      <div ref={wrapperRef} className="project-snapshots-wrapper">
        {mediaItems.map((media, index) => (
          <div
            key={index}
            className={`project-snapshot${enableLightbox ? ' is-clickable' : ''}`}
          >
            <button
              type="button"
              className="project-snapshot-button"
              onClick={() => handleSnapshotClick(media)}
              aria-label={`Vergroot project ${media.type === 'video' ? 'video' : 'afbeelding'} ${index + 1}`}
            >
              {media.type === 'video' ? (
                <video
                  src={media.src}
                  poster={media.poster || ''}
                  autoPlay={media.autoPlay}
                  loop={media.loop}
                  muted={media.muted}
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img src={media.src} alt={media.alt || `Project snapshot ${index + 1}`} />
              )}
              {enableLightbox && (
                <span className="snapshot-zoom-indicator" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    fill="none"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                  </svg>
                </span>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Progress bar wordt dynamisch toegevoegd via JavaScript */}
    </section>
  );
};

export default HorizontalScrollGallery;

